import React, { Suspense, useEffect, useMemo, useState } from "react";
import {
    Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
    DialogContent, DialogTitle, Divider, FormControlLabel, Grid, Paper, Radio,
    RadioGroup, Stack, Typography, TextField, InputAdornment, Pagination,
    MenuItem, Select, FormControl, InputLabel, Avatar,
} from "@mui/material";
import {
    Add as AddIcon, CheckCircle as CheckCircleIcon,
    DirectionsCar as DirectionsCarIcon, LocalTaxi as LocalTaxiIcon,
    Map as MapIcon, Person as PersonIcon, Search as SearchIcon,
    FilterList as FilterListIcon, Clear as ClearIcon,
    Warning as WarningIcon,
    Badge as BadgeIcon,
    Error as ErrorIcon,
    EditRoad as EditRoadIcon,
} from "@mui/icons-material";
import ManifestPassengersTable from "../../administration/manifests/components/ManifestPassengersTable";
import ManifestAssignRouteForm from "../../administration/manifests/components/route/ManifestAssignRouteForm";
import { usePasajeros, useRutasManifiesto } from "../../../hooks/useManifest";
import { useAssignTaxiTeam, useTaxiTeams, useUnassignTaxiTeam, useFinishTaxiManifest } from "../../../hooks/taxi/useTaxiManifest";
import { useCollaborators } from "../../../hooks/administration/useServiceCollaborators";
import { useAuth } from "../../../hooks/useAuth";
import TaxiTeamIntegratedForm from "./TaxiTeamIntegratedForm";

const RouteMap = React.lazy(() => import("../../administracion/rutas/RouteMap"));

const idOrNull = (value) => {
    const id = Number(value);
    return Number.isInteger(id) && id > 0 ? id : null;
};

const manifestIdOf = (manifest) => idOrNull(manifest?.manid ?? manifest?.ManID);
const orderIdOf = (manifest) => idOrNull(manifest?.ordsercabid ?? manifest?.OrdSerCabID);
const serviceIdOf = (manifest) => idOrNull(
    manifest?.serproid ?? manifest?.SerProID ?? manifest?.serProId
    ?? manifest?.servicioProyecto?.serproid ?? manifest?.servicioProyecto?.SerProID
);
const serviceNameOf = (manifest) => manifest?.serpronom ?? manifest?.SerProNom
    ?? manifest?.servicioProyecto?.serpronom ?? manifest?.servicioProyecto?.SerProNom ?? null;
const teamIdOf = (team) => idOrNull(team?.equConVehId ?? team?.equconvehid ?? team?.EquConVehID ?? team?.teamId);
const employeeIdOf = (team) => idOrNull(team?.empId ?? team?.empid ?? team?.EmpID);
const vehicleIdOf = (team) => idOrNull(team?.vehId ?? team?.vehid ?? team?.VehID);

const currentEmployeeIdOf = (manifest) => {
    const employees = Array.isArray(manifest?.empleados) ? manifest.empleados : [];
    return idOrNull(manifest?.empid ?? manifest?.EmpID ?? employees[0]?.empid ?? employees[0]?.EmpID);
};

const currentVehicleIdOf = (manifest) => idOrNull(
    manifest?.vehid ?? manifest?.VehID ?? manifest?.vehiculo?.vehid ?? manifest?.vehiculo?.VehID
);

const currentTeamIdOf = (manifest) => idOrNull(
    manifest?.equconvehid ?? manifest?.EquConVehID ?? manifest?.equConVehId
    ?? manifest?.equipo?.equconvehid ?? manifest?.equipo?.EquConVehID
);

const formatManifestCode = (id) => id ? `MN-${String(id).padStart(6, "0")}` : "Sin código";

const manifestStopsOf = (manifest) => {
    const stops =
        manifest?.paradas ??
        manifest?.stops ??
        manifest?.manifestStops ??
        [];

    if (!Array.isArray(stops)) {
        return [];
    }

    return stops
        .map((stop) => ({
            manparid:
                stop?.manparid ??
                stop?.ManParID ??
                null,

            locid: idOrNull(
                stop?.locid ??
                stop?.LocID
            ),

            locnom:
                stop?.locnom ??
                stop?.LocNom ??
                stop?.localizacion?.locnom ??
                "Sin nombre",

            locdes:
                stop?.locdes ??
                stop?.LocDes ??
                stop?.localizacion?.locdes ??
                "",

            loclat:
                stop?.loclat ??
                stop?.LocLat ??
                stop?.localizacion?.loclat ??
                null,

            loclng:
                stop?.loclng ??
                stop?.LocLng ??
                stop?.localizacion?.loclng ??
                null,

            partip:
                stop?.partip ??
                stop?.ParTip ??
                "INTERMEDIA",

            parord: Number(
                stop?.parord ??
                stop?.ParOrd ??
                0
            ),

            parhorest:
                stop?.parhorest ??
                stop?.ParHorEst ??
                null,
        }))
        .filter((stop) => stop.locid)
        .sort(
            (a, b) =>
                Number(a.parord) -
                Number(b.parord)
        );
};

const formatStopDateTime = (value) => {
    if (!value) {
        return "Sin hora";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return "Hora inválida";
    }

    return new Intl.DateTimeFormat(
        "es-PE",
        {
            timeZone: "America/Lima",
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
        }
    ).format(date);
};

const TaxiTeamOption = ({ team, selected, disabled }) => {
    const driverName = team?.driverName ?? team?.conductornombre ?? team?.ConductorNombre ?? "Conductor sin nombre";
    const driverDocument = team?.driverDocument ?? team?.conductordni ?? team?.ConductorDNI;
    const vehiclePlate = team?.vehiclePlate ?? team?.vehmat ?? team?.VehMat ?? "Sin placa";
    const vehicleModel = team?.vehicleModel ?? team?.vehmod ?? team?.VehMod ?? "Modelo no registrado";
    const teamName = team?.teamName ?? team?.equconvehnom ?? team?.EquConVehNom;
    const busy = Boolean(team?.busy);
    const current = Boolean(team?.current);
    const isSelected = selected;

    // Determinar el color de fondo basado en el estado
    const getBackgroundColor = () => {
        if (isSelected) return 'rgba(46, 125, 50, 0.08)'; // Verde claro
        if (busy && !current) return 'rgba(237, 108, 2, 0.08)'; // Naranja claro
        return 'background.paper';
    };

    const getBorderColor = () => {
        if (isSelected) return 'success.main';
        if (busy && !current) return 'warning.main';
        return 'divider';
    };

    // Obtener el estado de la tarjeta
    const getCardStatus = () => {
        if (isSelected) return { label: "Seleccionado", color: "success", icon: <CheckCircleIcon sx={{ fontSize: 14 }} /> };
        if (current) return { label: "Asignación actual", color: "info", icon: <CheckCircleIcon sx={{ fontSize: 14 }} /> };
        if (busy) return { label: "Ocupado", color: "warning", icon: <WarningIcon sx={{ fontSize: 14 }} /> };
        return null;
    };

    const status = getCardStatus();

    return (
        <Box
            sx={{
                position: 'relative',
                height: '100%',
                cursor: disabled ? 'not-allowed' : 'pointer',
            }}
            onClick={() => {
                if (!disabled) {
                    const radio = document.getElementById(`radio-${teamIdOf(team)}`);
                    if (radio) radio.click();
                }
            }}
        >
            <Paper
                elevation={isSelected ? 4 : busy ? 2 : 1}
                sx={{
                    height: '100%',
                    p: 2.5,
                    borderRadius: 3,
                    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                    backgroundColor: getBackgroundColor(),
                    border: `2px solid ${getBorderColor()}`,
                    position: 'relative',
                    opacity: disabled ? 0.6 : 1,
                    '&:hover': {
                        transform: !disabled ? 'translateY(-4px)' : 'none',
                        boxShadow: !disabled ? (isSelected ? 8 : 4) : 1,
                    },
                    '&:active': {
                        transform: !disabled ? 'scale(0.98)' : 'none',
                    },
                }}
            >
                {/* Status Badge - mejorado y bien posicionado */}
                {status && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: -10,
                            left: 16,
                            backgroundColor: `${status.color}.main`,
                            color: 'white',
                            px: 1.5,
                            py: 0.75,
                            borderRadius: 20,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                            boxShadow: 2,
                            fontSize: '0.6rem',
                            fontWeight: 'bold',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            zIndex: 1,
                        }}
                    >
                        {status.icon}
                        <span>{status.label}</span>
                    </Box>
                )}

                <FormControlLabel
                    value={String(teamIdOf(team))}
                    disabled={disabled}
                    control={
                        <Radio
                            id={`radio-${teamIdOf(team)}`}
                            sx={{
                                position: 'absolute',
                                top: 12,
                                right: 12,
                                '& .MuiSvgIcon-root': {
                                    fontSize: 24,
                                },
                            }}
                        />
                    }
                    sx={{
                        width: '100%',
                        m: 0,
                        alignItems: 'flex-start',
                        '& .MuiFormControlLabel-label': {
                            width: '100%',
                        },
                    }}
                    label={
                        <Stack spacing={1.5} sx={{ width: '100%', pt: 0.5, pr: 4.5 }}>
                            {/* Información del Conductor */}
                            <Stack direction="row" spacing={1.5} alignItems="center">
                                <Avatar
                                    sx={{
                                        width: 44,
                                        height: 44,
                                        bgcolor: isSelected ? 'success.main' : busy ? 'warning.main' : 'primary.main',
                                        flexShrink: 0,
                                    }}
                                >
                                    <PersonIcon sx={{ fontSize: 22 }} />
                                </Avatar>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                        variant="body2"
                                        fontWeight="bold"
                                        sx={{
                                            fontSize: '0.85rem',
                                            lineHeight: 1.2,
                                            mb: 0.25,
                                            color: isSelected ? 'success.dark' : 'text.primary',
                                        }}
                                    >
                                        {driverName}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            fontSize: '0.7rem',
                                        }}
                                    >
                                        <BadgeIcon sx={{ fontSize: 12 }} />
                                        DNI: {driverDocument || "No registrado"}
                                    </Typography>
                                </Box>
                            </Stack>

                            <Divider sx={{ my: 0.5 }} />

                            {/* Información del Vehículo */}
                            <Stack direction="row" spacing={1.5} alignItems="center">
                                <Avatar
                                    sx={{
                                        width: 36,
                                        height: 36,
                                        bgcolor: 'grey.100',
                                        flexShrink: 0,
                                    }}
                                >
                                    <DirectionsCarIcon sx={{ color: 'grey.600', fontSize: 18 }} />
                                </Avatar>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                        variant="body2"
                                        fontWeight="bold"
                                        sx={{
                                            fontSize: '0.85rem',
                                            lineHeight: 1.2,
                                        }}
                                    >
                                        {vehiclePlate}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{
                                            fontSize: '0.7rem',
                                        }}
                                    >
                                        {vehicleModel}
                                    </Typography>
                                </Box>
                            </Stack>

                            {/* Nombre del Equipo */}
                            {teamName && (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                        display: 'block',
                                        textAlign: 'center',
                                        fontSize: '0.7rem',
                                        bgcolor: isSelected ? 'success.light' : busy ? 'warning.light' : 'action.hover',
                                        py: 0.75,
                                        px: 1,
                                        borderRadius: 1.5,
                                        mt: 0.5,
                                        fontWeight: 500,
                                    }}
                                >
                                    {teamName}
                                </Typography>
                            )}

                            {/* Mensaje de ocupado - mejorado y más visible */}
                            {busy && team?.busyReason && !current && !isSelected && (
                                <Alert
                                    severity="warning"
                                    icon={<WarningIcon fontSize="small" />}
                                    sx={{
                                        py: 0.5,
                                        px: 1.5,
                                        fontSize: '0.7rem',
                                        borderRadius: 2,
                                        '& .MuiAlert-icon': {
                                            fontSize: 18,
                                            mr: 0.5,
                                            alignItems: 'center',
                                        },
                                        '& .MuiAlert-message': {
                                            padding: 0,
                                        },
                                    }}
                                >
                                    {team.busyReason}
                                </Alert>
                            )}
                        </Stack>
                    }
                />
            </Paper>
        </Box>
    );
};

const ITEMS_PER_PAGE = 12; // 4 columnas x 3 filas

const TaxiManifestOperationDialog = ({ open, manifest, onClose, onSuccess }) => {

    const { user } = useAuth();
    const currentEmpId = idOrNull(user?.EmpID ?? user?.empid);


    const [selectedTeamId, setSelectedTeamId] = useState("");
    const [localError, setLocalError] = useState("");
    const [openTeamForm, setOpenTeamForm] = useState(false);

    // Nuevos estados para paginación y filtros
    const [page, setPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterStatus, setFilterStatus] = useState("all");

    const [openRouteForm, setOpenRouteForm] = useState(false);

    const [confirmDialog, setConfirmDialog] = useState({
        open: false,
        title: '',
        message: '',
        confirmLabel: 'Confirmar',
        cancelLabel: 'Cancelar',
        onConfirm: null,
        severity: 'warning', // 'warning', 'error', 'info'
    });

    const manifestId = manifestIdOf(manifest);
    const ordsercabId = orderIdOf(manifest);
    const serProId = serviceIdOf(manifest);
    const serProName = serviceNameOf(manifest);
    const { pasajeros, isLoading: loadingPassengers, error: passengersError } = usePasajeros(manifestId, {
        enabled: Boolean(open && manifestId),
    });
    const { colaboradores, isLoading: loadingCollaborators, } = useCollaborators(ordsercabId);

    const {
        data: teams = [], isLoading: loadingTeams, error: teamsError, refetch: refetchTeams,
    } = useTaxiTeams({
        manifestId,
        serProId,
        enabled: Boolean(open && manifestId && serProId),
    });

    const assignMutation = useAssignTaxiTeam();
    const unassignMutation = useUnassignTaxiTeam();
    const finishMutation = useFinishTaxiManifest();

    const {
        paradas: manifestRouteStops = [],
        isLoading: loadingRouteStops,
        error: routeStopsError,
        assignRuta: routeMutation,
    } = useRutasManifiesto(manifestId);

    const manifestStops = useMemo(() => manifestStopsOf({ paradas: manifestRouteStops, }), [manifestRouteStops]);

    const originStop = useMemo(
        () =>
            manifestStops.find(
                (stop) =>
                    stop.partip === "ORIGEN"
            ) ?? manifestStops[0] ?? null,
        [manifestStops]
    );

    const hasOriginTime = Boolean(originStop?.parhorest);
    const currentTeam = useMemo(() => teams.find(team => team.current === true) || null, [teams]);

    // Filtrar equipos
    const filteredTeams = useMemo(() => {
        let result = teams;

        // Filtro por búsqueda
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase().trim();
            result = result.filter(team => {
                const driverName = (team?.driverName ?? team?.conductornombre ?? "").toLowerCase();
                const vehiclePlate = (team?.vehiclePlate ?? team?.vehmat ?? "").toLowerCase();
                const teamName = (team?.teamName ?? team?.equconvehnom ?? "").toLowerCase();
                const driverDoc = (team?.driverDocument ?? team?.conductordni ?? "").toLowerCase();
                return driverName.includes(term) ||
                    vehiclePlate.includes(term) ||
                    teamName.includes(term) ||
                    driverDoc.includes(term);
            });
        }

        // Filtro por estado
        if (filterStatus !== "all") {
            result = result.filter(team => {
                const isCurrent = teamIdOf(currentTeam) === teamIdOf(team) || Boolean(team?.current);
                const isBusy = Boolean(team?.busy);

                switch (filterStatus) {
                    case "available": return !isBusy && !isCurrent;
                    case "busy": return isBusy && !isCurrent;
                    case "current": return isCurrent;
                    default: return true;
                }
            });
        }

        return result;
    }, [teams, searchTerm, filterStatus, currentTeam]);

    // Paginación
    const totalPages = Math.ceil(filteredTeams.length / ITEMS_PER_PAGE);
    const paginatedTeams = useMemo(() => {
        const start = (page - 1) * ITEMS_PER_PAGE;
        const end = start + ITEMS_PER_PAGE;
        return filteredTeams.slice(start, end);
    }, [filteredTeams, page]);

    // Resetear página cuando cambian los filtros
    useEffect(() => {
        setPage(1);
    }, [searchTerm, filterStatus]);

    const selectedTeam = useMemo(
        () => teams.find((team) => String(teamIdOf(team)) === String(selectedTeamId)) ?? null,
        [teams, selectedTeamId]
    );

    const effectiveTeam = selectedTeam ?? currentTeam;
    const effectiveVehicleId = vehicleIdOf(effectiveTeam) ?? manifest?.vehid ?? manifest?.VehID ?? null;
    const hasCompleteAssignment = Boolean(currentTeam);
    const canEdit = ["CREADO", "PLANIFICADO"].includes(manifest?.manest);
    const canEditRoute = ["CREADO", "PLANIFICADO",].includes(manifest?.manest);
    const canRelease = Boolean(manifest?.manest === "CREADO" && effectiveTeam && serProId);

    useEffect(() => {
        if (!open) {
            setSelectedTeamId("");
            setLocalError("");
            setOpenTeamForm(false);
            setSearchTerm("");
            setFilterStatus("all");
            setPage(1);
            return;
        }
        const id = teamIdOf(currentTeam);
        setSelectedTeamId(id ? String(id) : "");
        setLocalError("");
    }, [open, currentTeam]);

    const executeAssignment = async ({ liberar }) => {
        setLocalError("");
        if (!manifestId) return setLocalError("El manifiesto no tiene un identificador válido.");
        if (!serProId) return setLocalError("El manifiesto no contiene SerProID.");

        const equConVehId = teamIdOf(effectiveTeam);
        if (!equConVehId) return setLocalError("Debe seleccionar un equipo registrado.");

        try {
            const result = await assignMutation.mutateAsync({ manifestId, equConVehId, liberar });
            onSuccess?.(
                result,
                liberar ? "Equipo asignado y manifiesto liberado." : "Equipo asignado correctamente."
            );
        } catch (error) {
            setLocalError(
                error?.response?.data?.error || error?.response?.data?.message
                || error?.message || "No se pudo actualizar el manifiesto."
            );
        }
    };

    const canFinish = useMemo(() => {
        if (!manifest) return false;
        // Solo si está EN_PROCESO y tiene equipo asignado
        return manifest.manest === "EN_PROCESO" && currentTeam !== null;
    }, [manifest, currentTeam]);

    const isComplete = manifest?.manest === "COMPLETADO" || manifest?.manest === "CANCELADO";

    // Función para manejar la finalización
    const handleFinishManifest = () => {
        if (!manifestId) return setLocalError("El manifiesto no tiene un identificador válido.");
        if (!currentTeam) return setLocalError("El manifiesto debe tener un equipo asignado.");

        const teamName = currentTeam?.teamName || currentTeam?.equconvehnom || 'actual';
        const passengerCount = pasajeros?.length || 0;

        openConfirmDialog(
            'Finalizar manifiesto',
            `¿Estás seguro de finalizar el manifiesto ${formatManifestCode(manifestId)}?\n\n` +
            `• Conductor: ${teamName}\n` +
            `• Pasajeros: ${passengerCount}\n\n` +
            `Los pasajeros en estado RESERVADO serán marcados como NO VIAJÓ.`,
            async () => {
                setLocalError("");
                try {
                    const result = await finishMutation.mutateAsync({ manifestId });
                    onSuccess?.(
                        result,
                        `Manifiesto finalizado correctamente. ${result.totalPassengers} pasajeros procesados.`
                    );
                } catch (error) {
                    setLocalError(
                        error?.response?.data?.error || error?.response?.data?.message
                        || error?.message || "No se pudo finalizar el manifiesto."
                    );
                }
            },
            'Sí, finalizar',
            'Cancelar',
            'success' // Ícono de éxito (verde)
        );
    };

    const handleUnassign = () => {
        if (!manifestId) return setLocalError("El manifiesto no tiene un identificador válido.");
        if (!currentTeam) return setLocalError("No hay equipo asignado para desasignar.");

        const teamName = currentTeam?.teamName || currentTeam?.equconvehnom || 'actual';
        openConfirmDialog(
            'Desasignar equipo',
            `¿Estás seguro de desasignar el equipo "${teamName}" del manifiesto?`,
            async () => {
                setLocalError("");
                try {
                    const result = await unassignMutation.mutateAsync({ manifestId });
                    onSuccess?.(
                        result,
                        `Equipo desasignado correctamente. El manifiesto quedó en estado ${result.newState}.`
                    );
                } catch (error) {
                    setLocalError(
                        error?.response?.data?.error || error?.response?.data?.message
                        || error?.message || "No se pudo desasignar el equipo."
                    );
                }
            },
            'Sí, desasignar',
            'Cancelar',
            'error'
        );
    };

    const handleTeamCreated = async (createdTeam, message) => {
        setOpenTeamForm(false);
        const refreshed = await refetchTeams();
        const list = refreshed?.data ?? [];
        const createdId = teamIdOf(createdTeam);
        if (createdId && list.some((team) => teamIdOf(team) === createdId)) {
            setSelectedTeamId(String(createdId));
        }
        onSuccess?.(createdTeam, message);
    };

    const close = () => {
        if (!assignMutation.isPending) onClose();
    };

    const clearFilters = () => {
        setSearchTerm("");
        setFilterStatus("all");
        setPage(1);
    };

    const openConfirmDialog = (title, message, onConfirm, confirmLabel = 'Confirmar', cancelLabel = 'Cancelar', severity = 'warning') => {
        setConfirmDialog({
            open: true,
            title,
            message,
            confirmLabel,
            cancelLabel,
            onConfirm: () => {
                onConfirm();
                setConfirmDialog(prev => ({ ...prev, open: false }));
            },
            severity,
        });
    };

    const handleAssignRoute = async (
        payload
    ) => {
        if (!manifestId) {
            setLocalError(
                "El manifiesto no tiene un identificador válido."
            );
            return;
        }

        if (
            !Array.isArray(
                payload?.paradas
            ) ||
            payload.paradas.length < 2
        ) {
            setLocalError(
                "La ruta debe contener al menos un origen y un destino."
            );
            return;
        }

        setLocalError("");

        try {
            const result =
                await routeMutation.mutateAsync({
                    manifiestoId:
                        manifestId,

                    payload: {
                        rutid:
                            payload.rutid ||
                            null,

                        updbyid:
                            currentEmpId,

                        paradas:
                            payload.paradas,
                    },
                });

            setOpenRouteForm(false);

            onSuccess?.(
                result,
                "Ruta y horarios actualizados correctamente."
            );
        } catch (error) {
            setLocalError(
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                error?.message ||
                "No se pudo actualizar la ruta."
            );
        }
    };

    return (
        <>
            <Dialog open={open} onClose={close} fullWidth maxWidth="lg" disableEscapeKeyDown={assignMutation.isPending}
                PaperProps={{ sx: { minHeight: { xs: "90vh", md: "80vh" } } }}>
                <DialogTitle>
                    <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={1}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <LocalTaxiIcon color="warning" />
                            <Box>
                                <Typography variant="h6" fontWeight="bold">Operación de taxi</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {formatManifestCode(manifestId)}
                                    {serProName ? ` · ${serProName}` : serProId ? ` · Servicio ${serProId}` : ""}
                                </Typography>
                            </Box>
                        </Stack>
                        <Chip label={manifest?.manest || "SIN ESTADO"} color={
                            manifest?.manest === "PLANIFICADO" ? "warning"
                                : manifest?.manest === "EN_PROCESO" ? "success"
                                    : manifest?.manest === "COMPLETADO" ? "info" : "default"
                        } sx={{ fontWeight: "bold" }} />
                    </Stack>
                </DialogTitle>

                <DialogContent dividers>
                    <Stack spacing={2.5}>
                        {!serProId && <Alert severity="error">El manifiesto no contiene SerProID.</Alert>}
                        {(localError || teamsError || passengersError || routeStopsError) && <Alert severity="error">
                            {localError ||
                                teamsError?.response?.data?.error ||
                                teamsError?.message ||
                                passengersError?.response?.data?.error ||
                                passengersError?.message ||
                                routeStopsError?.response?.data?.error ||
                                routeStopsError?.message ||
                                "Ocurrió un error."}
                        </Alert>}
                        {!canEdit && <Alert severity="info">
                            El manifiesto está en estado <strong>{manifest?.manest}</strong>. La asignación solo puede modificarse mientras esté creado o planificado.
                        </Alert>}

                        {/*<Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
                            <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2}>
                                <Box><Typography variant="subtitle2" color="text.secondary">Descripción</Typography><Typography fontWeight="bold">{manifest?.maninf || "Sin descripción"}</Typography></Box>
                                <Box><Typography variant="subtitle2" color="text.secondary">Ruta</Typography><Typography fontWeight="bold">{manifest?.manori || "Sin origen"} → {manifest?.mandes || "Sin destino"}</Typography></Box>
                                <Box><Typography variant="subtitle2" color="text.secondary">Orden de servicio</Typography><Typography fontWeight="bold">{manifest?.ordsercabid ? `OS-${String(manifest.ordsercabid).padStart(6, "0")}` : "Sin orden"}</Typography></Box>
                            </Stack>
                        </Paper>*/}
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 2,
                                borderRadius: 2.5,
                            }}
                        >
                            <Stack
                                direction={{
                                    xs: "column",
                                    md: "row",
                                }}
                                justifyContent="space-between"
                                spacing={2}
                            >
                                <Box>
                                    <Typography
                                        variant="subtitle2"
                                        color="text.secondary"
                                    >
                                        Descripción
                                    </Typography>

                                    <Typography fontWeight="bold">
                                        {manifest?.maninf ||
                                            "Sin descripción"}
                                    </Typography>
                                </Box>

                                <Box>
                                    <Typography
                                        variant="subtitle2"
                                        color="text.secondary"
                                    >
                                        Ruta
                                    </Typography>

                                    <Typography fontWeight="bold">
                                        {manifest?.manori ||
                                            "Sin origen"}{" "}
                                        →{" "}
                                        {manifest?.mandes ||
                                            "Sin destino"}
                                    </Typography>
                                </Box>

                                <Box>
                                    <Typography
                                        variant="subtitle2"
                                        color="text.secondary"
                                    >
                                        Hora de salida
                                    </Typography>

                                    {loadingRouteStops ? (
                                        <CircularProgress size={20} />
                                    ) : hasOriginTime ? (
                                        <Chip
                                            size="small"
                                            color="success"
                                            variant="outlined"
                                            label={formatStopDateTime(
                                                originStop.parhorest
                                            )}
                                        />
                                    ) : (
                                        <Chip
                                            size="small"
                                            color="warning"
                                            variant="outlined"
                                            icon={<WarningIcon />}
                                            label="Sin hora registrada"
                                        />
                                    )}
                                </Box>

                                <Box>
                                    <Typography
                                        variant="subtitle2"
                                        color="text.secondary"
                                    >
                                        Orden de servicio
                                    </Typography>

                                    <Typography fontWeight="bold">
                                        {manifest?.ordsercabid
                                            ? `OS-${String(
                                                manifest.ordsercabid
                                            ).padStart(6, "0")}`
                                            : "Sin orden"}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Paper>

                        <Box>
                            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={1} mb={1.5}>
                                <Box>
                                    <Typography variant="subtitle1" fontWeight="bold">Equipo conductor–vehículo</Typography>
                                    <Typography variant="body2" color="text.secondary">Solo se muestran equipos activos del servicio-proyecto del manifiesto.</Typography>
                                </Box>
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                    {hasCompleteAssignment && <Chip icon={<CheckCircleIcon />} label={currentTeam ? "Equipo registrado asignado" : "Asignación heredada"} color={currentTeam ? "success" : "warning"} variant="outlined" />}
                                    <Button size="small" variant="outlined" startIcon={<AddIcon />} disabled={!canEdit || !serProId} onClick={() => setOpenTeamForm(true)}>Crear equipo</Button>
                                </Stack>
                            </Stack>

                            {loadingTeams ? <Box display="flex" justifyContent="center" py={5}><CircularProgress /></Box>
                                : !serProId ? null
                                    : teams.length === 0 ? <Alert severity="warning">No existen equipos activos. Puede crear uno desde este diálogo.</Alert>
                                        : (
                                            <>
                                                {/* Filtros y búsqueda */}
                                                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={2}>
                                                    <TextField
                                                        size="small"
                                                        placeholder="Buscar por conductor, placa o equipo..."
                                                        value={searchTerm}
                                                        onChange={(e) => setSearchTerm(e.target.value)}
                                                        InputProps={{
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <SearchIcon fontSize="small" />
                                                                </InputAdornment>
                                                            ),
                                                            endAdornment: searchTerm && (
                                                                <InputAdornment position="end">
                                                                    <ClearIcon
                                                                        fontSize="small"
                                                                        sx={{ cursor: 'pointer' }}
                                                                        onClick={() => setSearchTerm("")}
                                                                    />
                                                                </InputAdornment>
                                                            )
                                                        }}
                                                        sx={{ flex: 1, minWidth: 200 }}
                                                    />

                                                    <FormControl size="small" sx={{ minWidth: 150 }}>
                                                        <InputLabel>Estado</InputLabel>
                                                        <Select
                                                            value={filterStatus}
                                                            onChange={(e) => setFilterStatus(e.target.value)}
                                                            label="Estado"
                                                            startAdornment={
                                                                <InputAdornment position="start">
                                                                    <FilterListIcon fontSize="small" />
                                                                </InputAdornment>
                                                            }
                                                        >
                                                            <MenuItem value="all">Todos</MenuItem>
                                                            <MenuItem value="available">Disponibles</MenuItem>
                                                            <MenuItem value="busy">Ocupados</MenuItem>
                                                            <MenuItem value="current">Asignación actual</MenuItem>
                                                        </Select>
                                                    </FormControl>

                                                    {(searchTerm || filterStatus !== "all") && (
                                                        <Button size="small" onClick={clearFilters} startIcon={<ClearIcon />}>
                                                            Limpiar filtros
                                                        </Button>
                                                    )}
                                                </Stack>

                                                {/* Contador de resultados */}
                                                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                                    {filteredTeams.length === 0 ? "No se encontraron equipos" :
                                                        `Mostrando ${((page - 1) * ITEMS_PER_PAGE) + 1} - ${Math.min(page * ITEMS_PER_PAGE, filteredTeams.length)} de ${filteredTeams.length} equipos`}
                                                </Typography>

                                                <RadioGroup
                                                    value={selectedTeamId}
                                                    onChange={(event) => {
                                                        setSelectedTeamId(event.target.value);
                                                        setLocalError("");
                                                    }}
                                                    sx={{
                                                        width: '100%',
                                                    }}
                                                >
                                                    {filteredTeams.length === 0 ? (
                                                        <Alert severity="info">No hay equipos que coincidan con los filtros aplicados.</Alert>
                                                    ) : (
                                                        <>
                                                            <Grid
                                                                container
                                                                spacing={2} // Aumentado para mejor separación
                                                                sx={{
                                                                    mt: 0.5,
                                                                }}
                                                            >
                                                                {paginatedTeams.map((team) => {
                                                                    const id = teamIdOf(team);
                                                                    const isCurrent = teamIdOf(currentTeam) === id || Boolean(team?.current);
                                                                    const isSelected = String(selectedTeamId) === String(id);

                                                                    return (
                                                                        <Grid
                                                                            item
                                                                            xs={12}
                                                                            sm={6}
                                                                            md={4}
                                                                            lg={3}
                                                                            key={id}
                                                                            sx={{
                                                                                display: 'flex',
                                                                            }}
                                                                        >
                                                                            <TaxiTeamOption
                                                                                team={team}
                                                                                selected={isSelected}
                                                                                disabled={!canEdit || (team?.busy && !isCurrent)}
                                                                            />
                                                                        </Grid>
                                                                    );
                                                                })}
                                                            </Grid>

                                                            {/* Paginación mejorada */}
                                                            {totalPages > 1 && (
                                                                <Box
                                                                    display="flex"
                                                                    justifyContent="center"
                                                                    alignItems="center"
                                                                    mt={3}
                                                                    pt={2}
                                                                    sx={{
                                                                        borderTop: '1px solid',
                                                                        borderColor: 'divider',
                                                                    }}
                                                                >
                                                                    <Stack direction="row" spacing={2} alignItems="center">
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            Página {page} de {totalPages}
                                                                        </Typography>
                                                                        <Pagination
                                                                            count={totalPages}
                                                                            page={page}
                                                                            onChange={(_, value) => setPage(value)}
                                                                            color="primary"
                                                                            size="medium"
                                                                            showFirstButton
                                                                            showLastButton
                                                                            siblingCount={1}
                                                                            boundaryCount={1}
                                                                            sx={{
                                                                                '& .MuiPaginationItem-root': {
                                                                                    fontWeight: 500,
                                                                                },
                                                                            }}
                                                                        />
                                                                    </Stack>
                                                                </Box>
                                                            )}
                                                        </>
                                                    )}
                                                </RadioGroup>
                                            </>
                                        )}
                        </Box>

                        <Box>
                            <Typography variant="subtitle1" fontWeight="bold" mb={1}>Pasajeros</Typography>
                            <ManifestPassengersTable
                                passengers={pasajeros}
                                loading={loadingPassengers}
                                canSelectPassengers={false}
                                manifestId={manifestId}
                            />
                        </Box>

                        <Box>
                            <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                                <MapIcon color="primary" />
                                <Typography variant="subtitle1" fontWeight="bold">Mapa de ruta</Typography>
                            </Stack>
                            {!manifestId ? <Alert severity="warning">No se puede cargar el mapa sin ManID.</Alert>
                                //: !effectiveVehicleId ? <Alert severity="info">Seleccione o asigne un equipo para mostrar el vehículo.</Alert>
                                : <Paper variant="outlined" sx={{ height: { xs: 420, md: 560 }, overflow: "hidden", borderRadius: 2 }}>
                                    <Suspense fallback={<Box display="flex" justifyContent="center" alignItems="center" height="100%"><CircularProgress /></Box>}>
                                        <RouteMap route={manifestId} vehid={effectiveVehicleId} />
                                    </Suspense>
                                </Paper>}
                        </Box>
                    </Stack>
                </DialogContent>                

                <DialogActions
                    sx={{
                        px: 3,
                        py: 2,
                        flexWrap: "wrap",
                        gap: 1,
                    }}
                >
                    <Button
                        onClick={close}
                        disabled={
                            assignMutation.isPending ||
                            unassignMutation.isPending ||
                            routeMutation.isPending
                        }
                    >
                        Cerrar
                    </Button>

                    <Box flexGrow={1} />

                    {canFinish && (
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={
                                finishMutation.isPending
                            }
                            startIcon={
                                finishMutation.isPending
                                    ? (
                                        <CircularProgress
                                            size={18}
                                            color="inherit"
                                        />
                                    )
                                    : <CheckCircleIcon />
                            }
                            onClick={handleFinishManifest}
                        >
                            {finishMutation.isPending
                                ? "Finalizando..."
                                : "Finalizar manifiesto"}
                        </Button>
                    )}

                    {canEdit && currentTeam && (
                        <Button
                            variant="outlined"
                            color="error"
                            disabled={
                                unassignMutation.isPending
                            }
                            startIcon={
                                unassignMutation.isPending
                                    ? (
                                        <CircularProgress
                                            size={18}
                                            color="inherit"
                                        />
                                    )
                                    : <ClearIcon />
                            }
                            onClick={handleUnassign}
                        >
                            {unassignMutation.isPending
                                ? "Desasignando..."
                                : "Desasignar equipo"}
                        </Button>
                    )}

                    <Button
                        variant={
                            hasOriginTime
                                ? "outlined"
                                : "contained"
                        }
                        color={
                            hasOriginTime
                                ? "primary"
                                : "warning"
                        }
                        startIcon={
                            routeMutation.isPending
                                ? (
                                    <CircularProgress
                                        size={18}
                                        color="inherit"
                                    />
                                )
                                : <EditRoadIcon />
                        }
                        disabled={
                            !canEditRoute ||
                            routeMutation.isPending
                        }
                        onClick={() =>
                            setOpenRouteForm(true)
                        }
                    >
                        {hasOriginTime
                            ? "Editar ruta y horarios"
                            : "Registrar hora de salida"}
                    </Button>

                    <Button
                        variant="outlined"
                        disabled={
                            !canEdit ||
                            !selectedTeam ||
                            assignMutation.isPending
                        }
                        onClick={() =>
                            executeAssignment({
                                liberar: false,
                            })
                        }
                    >
                        Guardar equipo
                    </Button>

                    <Button
                        variant="contained"
                        color="success"
                        disabled={
                            !canRelease ||
                            assignMutation.isPending ||
                            !hasOriginTime
                        }
                        startIcon={
                            assignMutation.isPending
                                ? (
                                    <CircularProgress
                                        size={18}
                                        color="inherit"
                                    />
                                )
                                : <CheckCircleIcon />
                        }
                        onClick={() =>
                            executeAssignment({
                                liberar: true,
                            })
                        }
                    >
                        {assignMutation.isPending
                            ? "Procesando..."
                            : "Asignar y liberar"}
                    </Button>
                </DialogActions>
            </Dialog>

            <TaxiTeamIntegratedForm
                open={openTeamForm}
                onClose={() => setOpenTeamForm(false)}
                onSuccess={handleTeamCreated}
                serProId={serProId}
                serviceName={serProName}
                organizations={colaboradores}
            />

            <ManifestAssignRouteForm
                open={openRouteForm}
                handleClose={() => {
                    if (!routeMutation.isPending) {
                        setOpenRouteForm(false);
                    }
                }}
                manifest={manifest}
                updByID={currentEmpId}
                defaultParadas={manifestStops}
                onAssigned={handleAssignRoute}
            />

            <Dialog
                open={confirmDialog.open}
                onClose={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {confirmDialog.severity === 'error' && <ErrorIcon color="error" />}
                    {confirmDialog.severity === 'warning' && <WarningIcon color="warning" />}
                    {confirmDialog.severity === 'success' && <CheckCircleIcon color="success" />}
                    {confirmDialog.title}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1">{confirmDialog.message}</Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 1 }}>
                    <Button onClick={() => setConfirmDialog(prev => ({ ...prev, open: false }))} variant="outlined">
                        {confirmDialog.cancelLabel}
                    </Button>
                    <Button
                        onClick={confirmDialog.onConfirm}
                        variant="contained"
                        color={
                            confirmDialog.severity === 'error' ? 'error' :
                                confirmDialog.severity === 'success' ? 'success' :
                                    'warning'
                        }
                        autoFocus
                    >
                        {confirmDialog.confirmLabel}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default TaxiManifestOperationDialog;